import { notifications } from '@mantine/notifications';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Form, HttpUpdateForm } from '@/api';
import { getErrorTitle } from '@/api/utils/error-title';
import { showApiErrorNotification } from '@/api/utils/notifications';
import { useUndoToast } from '@/components/Ui/Toast';
import { type FieldErrors, fieldErrorMessages, validationErrors } from '@/lib/field-errors';
import { useRustyState } from '@/state';

// What an absent value is sent as when undo has to restore "not set".
const CLEARED: Partial<Record<keyof HttpUpdateForm, unknown>> = {
  redirect_url: '',
  auto_response_subject: '',
  auto_response_text: '',
  allowed_origins: [],
};

/** Fields whose rows show a refusal under the field; any other refusal is a toast in plain words. */
export const INLINE_ERROR_FIELDS = new Set([
  'title',
  'redirect_url',
  'allowed_origins',
  'auto_response_enabled',
  'auto_response_subject',
  'auto_response_text',
]);

/** The patch that restores `form` for the keys in `patch`, or null when a key cannot be cleared. */
function undoPatch(form: Form, patch: HttpUpdateForm): HttpUpdateForm | null {
  const previous: Record<string, unknown> = {};
  for (const key of Object.keys(patch) as (keyof HttpUpdateForm)[]) {
    const value = (form as unknown as Record<string, unknown>)[key];
    if (value !== undefined && value !== null) {
      previous[key] = value;
    } else if (key in CLEARED) {
      previous[key] = CLEARED[key];
    } else {
      return null;
    }
  }
  return previous as HttpUpdateForm;
}

function pick(from: Form, keys: string[]): Partial<Form> {
  const row = from as unknown as Record<string, unknown>;
  return Object.fromEntries(keys.map((key) => [key, row[key]])) as Partial<Form>;
}

function applyPatch(form: Form, patch: HttpUpdateForm): Form {
  return { ...form, ...(patch as Partial<Form>) };
}

export interface InstantSave {
  /** The form with pending saves applied; follows `form` whenever the caller passes a new object. */
  form: Form | null;
  /** Optimistic PATCH plus undo toast; `false` after a rollback (throw on it inside InlineTextEditor's onSave). */
  save: (patch: HttpUpdateForm, message: string) => Promise<boolean>;
  saving: boolean;
  /** The last refusal per field, cleared by the next save. */
  errors: FieldErrors;
  /** For checks made before sending, so they show in the same place as the server's. */
  setErrors: (errors: FieldErrors) => void;
}

export function useInstantSave(
  form: Form | null | undefined,
  onSaved?: (next: Form) => void
): InstantSave {
  const { t } = useTranslation();
  const notifyWithUndo = useUndoToast();
  const [current, setCurrent] = useState<Form | null>(form ?? null);
  const [source, setSource] = useState(form);
  const [pending, setPending] = useState(0);
  const [errors, setErrors] = useState<FieldErrors>({});
  const sentSeq = useRef(0);
  const touchedBy = useRef(new Map<string, number>());
  const inflight = useRef(new Set<number>());
  const latest = useRef<Form | null>(current);
  const confirmed = useRef<Form | null>(form ?? null);

  if (form !== source) {
    setSource(form);
    setCurrent(form ?? null);
  }

  useEffect(() => {
    latest.current = current;
  }, [current]);

  useEffect(() => {
    confirmed.current = source ?? null;
  }, [source]);

  const set = useCallback((next: Form) => {
    latest.current = next;
    setCurrent(next);
  }, []);

  const report = useCallback(
    (e: unknown) => {
      const raw = validationErrors(e);
      if (!raw) {
        // Without a response the request layer has already reported the connection error.
        if ((e as { response?: unknown })?.response) {
          showApiErrorNotification(e, notifications, getErrorTitle(e as never));
        }
        return;
      }
      const messages = fieldErrorMessages(raw, t);
      const inline: FieldErrors = {};
      const other: string[] = [];
      for (const [field, message] of Object.entries(messages)) {
        if (!message) continue;
        if (INLINE_ERROR_FIELDS.has(field)) inline[field] = message;
        else other.push(message);
      }
      setErrors(inline);
      if (other.length > 0) {
        notifications.show({
          color: 'red',
          title: t('form.fieldErrors.notSaved'),
          message: other.join(' '),
        });
      }
    },
    [t]
  );

  const send = useCallback(
    async (patch: HttpUpdateForm): Promise<boolean> => {
      const before = latest.current;
      if (!before) return false;
      sentSeq.current += 1;
      const seq = sentSeq.current;
      const keys = Object.keys(patch);
      for (const key of keys) touchedBy.current.set(key, seq);
      const owned = () => keys.filter((key) => touchedBy.current.get(key) === seq);
      inflight.current.add(seq);
      set(applyPatch(before, patch));
      setErrors({});
      setPending((n) => n + 1);
      try {
        const { api, refreshProblemsCount } = useRustyState.getState();
        const saved = await api
          .updateFormPartial(before.id, patch, { onSuccess: false, onError: false })
          .finally(() => inflight.current.delete(seq));
        if (saved?.id === before.id) {
          if (seq === sentSeq.current) {
            // The whole row carries server-stamped fields such as check_challenge_since.
            confirmed.current = saved;
            const open = [...touchedBy.current].filter(([, w]) => inflight.current.has(w));
            const openKeys = open.map(([key]) => key);
            set({ ...saved, ...pick(latest.current ?? saved, openKeys) });
          } else {
            const mine = pick(saved, owned());
            confirmed.current = { ...(confirmed.current ?? saved), ...mine };
            if (latest.current) set({ ...latest.current, ...mine });
          }
        }
        if (latest.current) onSaved?.(latest.current);
        void refreshProblemsCount();
        return true;
      } catch (e) {
        if (latest.current)
          set({ ...latest.current, ...pick(confirmed.current ?? before, owned()) });
        report(e);
        return false;
      } finally {
        setPending((n) => n - 1);
      }
    },
    [onSaved, set, report]
  );

  const save = useCallback(
    async (patch: HttpUpdateForm, message: string): Promise<boolean> => {
      const before = latest.current;
      if (!before) return false;
      const previous = undoPatch(before, patch);
      const ok = await send(patch);
      if (ok) {
        notifyWithUndo(
          message,
          previous
            ? async () => {
                await send(previous);
              }
            : undefined
        );
      }
      return ok;
    },
    [notifyWithUndo, send]
  );

  return { form: current, save, saving: pending > 0, errors, setErrors };
}
